import {
  AlertRow,
  FieldFacet,
  FieldSchema,
  HistogramBucket,
  SavedSearch,
} from '../models/data-house.models';

// === Schema ===

export const mockSchema: FieldSchema[] = [
  { field: 'tracking_id', label: 'Tracking ID', type: 'keyword', faceted: false },
  { field: 'customer', label: 'Customer', type: 'text', faceted: false },
  { field: 'event_name', label: 'Event', type: 'text', faceted: true },
  { field: 'event_occurred_on', label: 'Occurred', type: 'date', faceted: false },
  { field: 'severity', label: 'Severity', type: 'keyword', faceted: true },
  { field: 'source_ip', label: 'Source IP', type: 'ip', faceted: true },
  { field: 'destination_ip', label: 'Destination IP', type: 'ip', faceted: true },
  { field: 'mitre_tactic', label: 'MITRE Tactic', type: 'keyword', faceted: true },
  { field: 'case_id', label: 'SOAR Case ID', type: 'number', faceted: false },
  { field: 'closure_reason', label: 'Closure Reason', type: 'keyword', faceted: true },
  { field: 'case_source', label: 'Source', type: 'keyword', faceted: true },
  { field: 'tags', label: 'Tags', type: 'keyword', faceted: true },
];

// === Generated alert rows (deterministic for reproducible mocks) ===

const SEVERITIES: AlertRow['severity'][] = ['critical', 'high', 'medium', 'low'];
const EVENTS = [
  'Suspicious PowerShell Execution',
  'Malware Detection',
  'Failed Login Burst',
  'Privilege Escalation Attempt',
  'Anomalous DNS Lookup',
  'Lateral Movement Detected',
  'Credential Dumping',
  'Data Exfiltration Pattern',
  'Ransomware Indicator',
  'Phishing URL Clicked',
];
const TACTICS = [
  'Initial Access',
  'Execution',
  'Persistence',
  'Privilege Escalation',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Exfiltration',
  'Command and Control',
];
const SOURCES = ['CrowdStrike Falcon', 'SentinelOne', 'Microsoft Defender', 'Palo Alto Cortex', 'Sumo Logic'];
const CLOSURES = ['True Positive', 'False Positive', 'Benign', 'Suppressed', 'Investigating'];
const CUSTOMERS = ['Acme Corp', 'Globex', 'Initech', 'Umbrella'];

/** Simple seeded RNG so the dataset is stable across reloads. */
function seededRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function genIp(rng: () => number, prefix?: string): string {
  if (prefix) return `${prefix}.${Math.floor(rng() * 256)}.${Math.floor(rng() * 256)}`;
  return `${Math.floor(rng() * 256)}.${Math.floor(rng() * 256)}.${Math.floor(rng() * 256)}.${Math.floor(rng() * 256)}`;
}

function generateAlerts(count: number): AlertRow[] {
  const rng = seededRng(42);
  // Anchor data to a fixed window so the histogram looks meaningful across the date range.
  const now = new Date('2026-06-09T00:00:00Z').getTime();
  const fortyFiveDaysMs = 45 * 24 * 60 * 60 * 1000;
  const rows: AlertRow[] = [];
  for (let i = 0; i < count; i++) {
    const occurred = new Date(now - rng() * fortyFiveDaysMs).toISOString();
    const severity = SEVERITIES[Math.floor(rng() * SEVERITIES.length)];
    const event = EVENTS[Math.floor(rng() * EVENTS.length)];
    const tactic = TACTICS[Math.floor(rng() * TACTICS.length)];
    const source = SOURCES[Math.floor(rng() * SOURCES.length)];
    const closure = rng() > 0.3 ? CLOSURES[Math.floor(rng() * CLOSURES.length)] : null;
    const sourceIp = genIp(rng, rng() > 0.5 ? '10.0' : undefined);
    const destIp = genIp(rng);
    const customer = CUSTOMERS[Math.floor(rng() * CUSTOMERS.length)];
    const trackingId = `evt-${(1000000 + i).toString(16)}`;
    rows.push({
      tracking_id: trackingId,
      customer,
      event_name: event,
      event_occurred_on: occurred,
      severity,
      source_ip: sourceIp,
      destination_ip: destIp,
      mitre_tactic: tactic,
      case_id: rng() > 0.4 ? Math.floor(rng() * 90000) + 10000 : null,
      closure_reason: closure,
      case_source: source,
      tags: rng() > 0.6 ? ['triaged', source.split(' ')[0].toLowerCase()] : [],
      raw_event: {
        tracking_id: trackingId,
        host: `host-${Math.floor(rng() * 200)}.corp.local`,
        process: rng() > 0.5 ? 'powershell.exe' : 'cmd.exe',
        user: `user${Math.floor(rng() * 50)}@${customer.toLowerCase().replace(/\s+/g, '')}.com`,
        command_line: 'Invoke-Expression (New-Object Net.WebClient).DownloadString(...)',
        parent_process: 'explorer.exe',
        hash_sha256: `${Math.floor(rng() * 1e16).toString(16).padStart(16, '0')}`,
      },
    });
  }
  return rows.sort((a, b) => (a.event_occurred_on < b.event_occurred_on ? 1 : -1));
}

export const mockAlerts: AlertRow[] = generateAlerts(500);

// === Histogram (aggregated from mockAlerts, day bucket) ===

function aggregateHistogram(rows: AlertRow[]): HistogramBucket[] {
  const buckets = new Map<string, number>();
  for (const row of rows) {
    const day = row.event_occurred_on.substring(0, 10);
    buckets.set(day, (buckets.get(day) ?? 0) + 1);
  }
  return Array.from(buckets.entries())
    .map(([bucket, count]) => ({ bucket: `${bucket}T00:00:00Z`, count }))
    .sort((a, b) => (a.bucket < b.bucket ? -1 : 1));
}

export const mockHistogram: HistogramBucket[] = aggregateHistogram(mockAlerts);

// === Facets (top values per faceted field) ===

function topValues(rows: AlertRow[], field: keyof AlertRow, limit = 10): Array<{ value: string | number; count: number }> {
  const counts = new Map<string | number, number>();
  for (const row of rows) {
    const v = row[field];
    if (v == null) continue;
    if (Array.isArray(v)) {
      for (const item of v) counts.set(item, (counts.get(item) ?? 0) + 1);
    } else if (typeof v === 'string' || typeof v === 'number') {
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export const mockFacets: FieldFacet[] = [
  { field: 'severity', label: 'Severity', values: topValues(mockAlerts, 'severity') },
  { field: 'mitre_tactic', label: 'MITRE Tactic', values: topValues(mockAlerts, 'mitre_tactic') },
  { field: 'case_source', label: 'Source', values: topValues(mockAlerts, 'case_source') },
  { field: 'event_name', label: 'Event', values: topValues(mockAlerts, 'event_name') },
  { field: 'closure_reason', label: 'Closure Reason', values: topValues(mockAlerts, 'closure_reason') },
];

// === Saved searches ===

export const mockSavedSearches: SavedSearch[] = [
  {
    id: 1,
    name: 'Privilege escalation, last 30 days',
    created_at: '2026-05-12T09:14:00Z',
    query: {
      dataset: 'alerts',
      timeRange: {
        start: '2026-05-10T00:00:00Z',
        end: '2026-06-09T00:00:00Z',
      },
      structuredFilters: { mitre_tactic: ['Privilege Escalation'] },
      freeText: '',
      sort: { field: 'event_occurred_on', dir: 'desc' },
    },
  },
  {
    id: 2,
    name: 'Critical from 10.0/16, all open cases',
    created_at: '2026-05-18T15:22:00Z',
    query: {
      dataset: 'alerts',
      timeRange: {
        start: '2026-05-02T00:00:00Z',
        end: '2026-06-09T00:00:00Z',
      },
      structuredFilters: { severity: ['critical'] },
      freeText: 'source_ip:10.0.* AND closure_reason:Investigating',
      sort: { field: 'event_occurred_on', dir: 'desc' },
    },
  },
];
